import { Component, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, concatMap, from, throwError } from 'rxjs';
import { Router, RouterModule } from '@angular/router';
import { fadeInOut } from '../../../directivas/animaciones/fade-animation';
import { AuthService } from '../../data-access/auth.service';
import { environment } from '../../../../environments/environment';
import { StorageService } from 'core-tsi';
import { ThemeService } from '../../../layouts/data-access/theme.service';
import { ToastService } from 'ng-components-tsi';
import { LOGIN_CODSUC_KEY, NAME_PROYECTO } from '../../utils/constantes';
import { RecaptchaV19Service } from 'recaptcha-v19';
import { withSpinner } from '../utils/manejar-loading';
import { buildVersionDate, buildVersionNumber } from '../../../../environments/version';
import { themeMap } from '@shared/modals/general/features/md-panel-control/utils/constantes';
import { AuthComponent, AuthSender, LoginEvent, LoginTypeEvent } from 'login-tsi';
import { hasSuccess } from '../utils/manejo-events';

/** Producto que se envía en el login (permisos del backend), igual que tesorería envía 'TESORERIA'. Coincide con NombresModulos.PRODUCCION (módulo 15). */
export const MODULO_APP = 'PRODUCCION'

const LOGIN_HINT_KEY = 'tsi_login_hint'

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, RouterModule, AuthComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', '../../auth.scss'],
  animations: [fadeInOut]
})

export class LoginComponent {

  private routes = inject(Router)
  private toastService = inject(ToastService)
  private formBuilder = inject(FormBuilder)
  private recatpchaService = inject(RecaptchaV19Service)
  private authService = inject(AuthService)
  private themeService = inject(ThemeService)

  produccion: boolean = environment.production
  versionApp = `${buildVersionDate ?? ''} ${buildVersionNumber ?? ''}`
  nombreProyecto = NAME_PROYECTO
  module = MODULO_APP

  readonly aboutList: string[] = [
    'Órdenes de producción, trabajo y pedidos de materiales',
    'Guías internas de consumo, ingreso y servicios',
    'Liquidación y cálculo de costos de producción',
    'Certificados de calidad y reportes en tiempo real'
  ]

  authForm = this.formBuilder.group({
    ruc: ['', Validators.required],
    nomuser: ['', Validators.required],
    clave: ['', Validators.required],
  })

  usersesion = input<string>('')

  ngOnInit(): void {
    if (this.usersesion()) {
      const decode = this.decodeUri(this.usersesion())
      if (decode) {
        this.completeLogin(decode.ruc, decode.coduser)
        this.saveCodsucLink(decode.codsuc)
        return
      }
    }
    this.loadLoginHint()
  }

  loadLoginHint = () => {
    const raw = localStorage.getItem(LOGIN_HINT_KEY)
    if (!raw) return
    try {
      const { ruc, coduser } = JSON.parse(raw)
      if (ruc && coduser) this.completeLogin(ruc, coduser)
    } catch { }
  }

  decodeUri = (encoded: string) => {
    try {
      const decoded = atob(encoded)
      const parsed = JSON.parse(decoded)
      return parsed.ruc && parsed.coduser ? { ruc: parsed.ruc, coduser: parsed.coduser, codsuc: parsed.codsuc } : null
    } catch (e) {
      console.error('Error al decodificar usersesion:', e)
      return null
    }
  }

  completeLogin = (ruc: string, nomuser: string) => this.authForm.patchValue({ruc,nomuser})

  saveCodsucLink = (codsuc: unknown) => {
    try {
      const value = Number(codsuc)
      value > 0 ? sessionStorage.setItem(LOGIN_CODSUC_KEY, String(value)) : sessionStorage.removeItem(LOGIN_CODSUC_KEY)
    } catch { }
  }

  onBeforeInstall = () => {
    const ruc = this.authForm.get('ruc')?.value
    const coduser = this.authForm.get('nomuser')?.value
    if (!ruc || !coduser) return
    localStorage.setItem(LOGIN_HINT_KEY, JSON.stringify({ ruc, coduser }))
    const payload = btoa(JSON.stringify({ ruc, coduser }))
    this.routes.navigate(['/login', payload], { replaceUrl: true })
  }

  auth = (event: LoginEvent) => {
    const { type, data, message } = event
    if (!hasSuccess(message, type, this.toastService)) return
    this.authResolver(type, data)
  }

  authResolver = (type:LoginTypeEvent, data: AuthSender) => {
    if(type == 'RECOVER') {
      this.routes.navigate(['/recuperar-clave'])
      return
    }
    if(type == 'FORGET') {
      this.routes.navigate(['/clave-expirada'])
      return
    }
    this.authApi(data)
  }

  authApi(data: AuthSender) {
    from(this.recatpchaService
      .execute('importantAction'))
      .pipe(
        withSpinner(),
        catchError((err) => {
          const transformed = { error: err?.message || err }
          return throwError(() => transformed)
        }),
        concatMap(() => {
          return this.authService.auth(data)
        })
      )
      .subscribe({
        next: (res) => {
          if (res.status != 200) {
            return
          }
          let session: any
          session = StorageService.getSession()
          if (!session) {
            return
          }
          const mappedTheme = themeMap[session.themes] || 'light';
          this.themeService.setTheme(mappedTheme);

          this.authService.getGlosaByCompany(session.id_cia, '50')
            .catch(err => console.error('Error al cargar la glosa 50 de la compañía:', err))
          this.routes.navigate(['seleccionar-sucursal'])
        },
        error: (err) => {
          this.toastService.error('Error', err.error)
        },
      })
  }
}
